import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Space
} from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { membershipService } from '../../../services';
import {MemberUpdateItem, MemGetMemberResponse, OthersTabProps} from "../../../model";
import SmartFormItem from "../../../components/common/SmartFormItem";
import EmailFormItem from "../../../components/common/EmailFormItem";

const { TextArea } = Input;

const OthersTab: React.FC<OthersTabProps> = ({
  memberData,
  onDataChanged,
  changedFields,
  onSaveSuccess,
  isEditing
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 初始化表單值 - 當 memberData 更新時，重新設置表單內容
  useEffect(() => {
    // 重新設置表單值為最新的 memberData
    const formValues = {
      ...memberData,
    };
    
    form.setFieldsValue(formValues);
    
    // 清除所有變更標記，因為此時 memberData 已更新，代表已經儲存成功
    changedFields.forEach(field => {
      onDataChanged(field, false);
    });
  }, [memberData, form, changedFields, onDataChanged]);

  // 處理表單欄位變更
  const handleFieldsChange = (changedFields: any, allFields: any) => {
    if (!isEditing) return;

    // 確認每個有變更的欄位
    changedFields.forEach((field: any) => {
      const fieldName = field.name[0];
      const fieldValue = field.value;

      // 取得原始值（會員資料來自後端，代表最後儲存成功的資料）
      const originalValue = memberData[fieldName as keyof MemGetMemberResponse];

      // 處理 undefined 和 null
      const isChanged = (originalValue === null || originalValue === undefined)
          ? !!fieldValue
          : fieldValue !== originalValue;

      // 將變更狀態通知父元件
      onDataChanged(fieldName, isChanged);
    });
  };

  // 儲存變更
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // 如果沒有變更欄位，直接返回
      if (changedFields.size === 0) {
        message.info('沒有需要儲存的變更');
        return;
      }
      
      setLoading(true);
      
      // 儲存前備份原始表單資料，用於失敗時還原
      const originalFormValues = form.getFieldsValue();
      
      // 清除不需要的欄位
      const updateData: MemberUpdateItem = {
        membershipId: memberData.membershipId,
        memberId: memberData.memberId,
        companyName: memberData.companyName,
        // 確保日期欄位沒有問題
        collectionAgent: values.collectionAgent,
        actualCapabilities: values.actualCapabilities,
        membershipContact: values.membershipContact,
        membershipContactPhone: values.membershipContactPhone,
        membershipContactEmail: values.membershipContactEmail,
        membershipContact_2: values.membershipContact2,
        membershipContactPhone_2: values.membershipContactPhone2,
        membershipContactEmail_2: values.membershipContactEmail2,
        committeeContact: values.committeeContact,
        committeeContactPhone: values.committeeContactPhone,
        committeeContactEmail: values.committeeContactEmail,
        exhibitionContact: values.exhibitionContact,
        exhibitionContactPhone: values.exhibitionContactPhone,
        exhibitionContactEmail: values.exhibitionContactEmail,
        remarks: values.remarks,
      };

      // 儲存資料到後端
      const response = await membershipService.updateMember(
          {
            members: [
              {
                ...memberData,
                ...updateData
              }]
          }
      );
      
      if (response && response.updatedCount > 0) {
        message.success('會員其他資料已更新成功');
        onSaveSuccess(); // 這將觸發父元件的刷新，獲取最新資料
      } else if (response) {
        message.error('會員其他資料更新失敗');
        // 儲存失敗則還原表單原始資料
        form.setFieldsValue(originalFormValues);
      }
    } catch (error) {
      console.error('Update member other info failed:', error);
      message.error('會員其他資料更新發生錯誤');
      // 發生錯誤時也還原原始資料
      handleReset();
    } finally {
      setLoading(false);
      // 無論儲存成功或失敗，最後都設為非編輯狀態
      // 注意：這裡只是設置本地編輯狀態，實際控制應該由父元件處理
      // 如需完整實現，需要在props中增加setIsEditing函數
      // 暫時註解以確保與現有架構相容
      // setIsEditing(false);
    }
  };

  // 重置表單 - 使用最後儲存成功的資料（memberData）進行重置
  const handleReset = () => {
    // 重新設置表單值為最新的 memberData
    const formValues = {
      ...memberData,
    };
    
    form.setFieldsValue(formValues);
    
    // 清除所有變更標記
    changedFields.forEach(field => {
      onDataChanged(field, false);
    });
  };

  // 判斷欄位是否有變更
  const isFieldChanged = (fieldName: string) => {
    return changedFields.has(fieldName);
  };

  return (
    <Card>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        onFieldsChange={handleFieldsChange}
        initialValues={{
          ...memberData,
        }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="實際能力或設備" 
              name="actualCapabilities"
              validateStatus={isFieldChanged('actualCapabilities') ? 'warning' : ''}
              help={isFieldChanged('actualCapabilities') ? '此欄位已修改' : ''}
            >
              <Input.TextArea rows={2} placeholder="請輸入實際能力或設備" disabled={!isEditing} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="會籍聯絡人" 
              name="membershipContact"
              validateStatus={isFieldChanged('membershipContact') ? 'warning' : ''}
              help={isFieldChanged('membershipContact') ? '此欄位已修改' : ''}
            >
              <Input placeholder="請輸入會籍聯絡人" disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <SmartFormItem
              label="會籍聯絡人手機" 
              name="membershipContactPhone"
              rules={[
                {
                  pattern: /^[0-9+() ~#-]*$/,
                  message: '只能輸入數字與符號',
                },
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入會籍聯絡人手機" disabled={!isEditing} />
            </SmartFormItem>
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <EmailFormItem
              label="會籍聯絡人e-mail" 
              name="membershipContactEmail"
              rules={[
                {
                  type: 'email',
                  message: '請輸入正確的信箱格式'
                }
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入會籍聯絡人電子郵件" disabled={!isEditing} />
            </EmailFormItem>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item
                label="會籍聯絡人2"
                name="membershipContact2"
                validateStatus={isFieldChanged('membershipContact2') ? 'warning' : ''}
                help={isFieldChanged('membershipContact2') ? '此欄位已修改' : ''}
            >
              <Input placeholder="請輸入會籍聯絡人" disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <SmartFormItem
              label="會籍聯絡人手機2"
              name="membershipContactPhone2"
              rules={[
                {
                  pattern: /^[0-9+() ~#-]*$/,
                  message: '只能輸入數字與符號',
                },
              ]}
            >
              <Input placeholder="請輸入會籍聯絡人手機" disabled={!isEditing} />
            </SmartFormItem>
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <EmailFormItem
              label="會籍聯絡人e-mail2"
              name="membershipContactEmail2"
              rules={[
                {
                  type: 'email',
                  message: '請輸入正確的信箱格式'
                }
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入會籍聯絡人電子郵件" disabled={!isEditing} />
            </EmailFormItem>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="專委會聯絡人" 
              name="committeeContact"
              validateStatus={isFieldChanged('committeeContact') ? 'warning' : ''}
              help={isFieldChanged('committeeContact') ? '此欄位已修改' : ''}
            >
              <Input placeholder="請輸入專委會聯絡人" disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <SmartFormItem
              label="專委會聯絡人手機" 
              name="committeeContactPhone"
              rules={[
                {
                  pattern: /^[0-9+() ~#-]*$/,
                  message: '只能輸入數字與符號',
                },
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入專委會聯絡人手機" disabled={!isEditing} />
            </SmartFormItem>
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <EmailFormItem
              label="專委會聯絡人e-mail" 
              name="committeeContactEmail"
              rules={[
                {
                  type: 'email',
                  message: '請輸入正確的信箱格式'
                }
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入專委會聯絡人電子郵件" disabled={!isEditing} />
            </EmailFormItem>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="展覽會聯絡人" 
              name="exhibitionContact"
              validateStatus={isFieldChanged('exhibitionContact') ? 'warning' : ''}
              help={isFieldChanged('exhibitionContact') ? '此欄位已修改' : ''}
            >
              <Input placeholder="請輸入展覽會聯絡人" disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <SmartFormItem
              label="展覽會聯絡人手機" 
              name="exhibitionContactPhone"
              rules={[
                {
                  pattern: /^[0-9+() ~#-]*$/,
                  message: '只能輸入數字與符號',
                },
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入展覽會聯絡人手機" disabled={!isEditing} />
            </SmartFormItem>
          </Col>
        </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <EmailFormItem
              label="展覽會聯絡人e-mail" 
              name="exhibitionContactEmail"
              rules={[
                {
                  type: 'email',
                  message: '請輸入正確的信箱格式'
                }
              ]}
              isFieldChanged={isFieldChanged}
            >
              <Input placeholder="請輸入展覽會聯絡人電子郵件" disabled={!isEditing} />
            </EmailFormItem>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="備註" 
              name="remarks"
              validateStatus={isFieldChanged('remarks') ? 'warning' : ''}
              help={isFieldChanged('remarks') ? '此欄位已修改' : ''}
            >
              <TextArea rows={2} placeholder="請輸入備註" disabled={!isEditing} maxLength={200}/>
            </Form.Item>
          </Col>
        </Row>

        {isEditing && (
          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Button 
                icon={<UndoOutlined />} 
                onClick={handleReset}
                disabled={changedFields.size === 0}
              >
                重置
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                onClick={handleSave}
                loading={loading}
                disabled={changedFields.size === 0}
              >
                儲存變更
              </Button>
            </Space>
          </Row>
        )}
      </Form>
    </Card>
  );
};

export default OthersTab;
